import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingCart, User, MapPin, Phone, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { COMPANY, LOGO_URL, LP_LOGO_URL, PRODUCTS } from "../data/mock";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const Header = () => {
  const { count } = useCart();
  const { user, signOut } = useAuth();
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    const q = query.toLowerCase();
    const match = PRODUCTS.find((p) => p.name.toLowerCase().includes(q) || p.slug.includes(q));
    if (match) navigate(`/product/${match.slug}`);
    else navigate(`/shop?q=${encodeURIComponent(query)}`);
  };

  return (
    <header className="w-full bg-white border-b border-zinc-100 sticky top-0 z-50">
      {/* top bar */}
      <div className="bg-zinc-50 border-b border-zinc-100 text-xs text-zinc-600">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-rose-500" />
            <span>{COMPANY.address}</span>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <span>{COMPANY.hours}</span>
            <span className="text-zinc-300">|</span>
            <Link to="/contact" className="hover:text-rose-500 transition-colors">Contact</Link>
            <Link to="/promo" className="hover:text-rose-500 transition-colors">Monthly Promo</Link>
          </div>
        </div>
      </div>

      {/* main bar */}
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-6">
        <Link to="/" className="flex items-center shrink-0">
          <span className="relative inline-block h-12 w-[180px]" aria-label="Design Plus — now Let's Print">
            <img src={LOGO_URL} alt="Design Plus" className="logo-dp absolute inset-0 m-auto h-12 w-auto max-w-full object-contain" />
            <img src={LP_LOGO_URL} alt="Let's Print" className="logo-lp absolute inset-0 m-auto h-12 w-auto max-w-full object-contain" />
          </span>
        </Link>

        <form onSubmit={onSearch} className="flex-1 max-w-2xl hidden md:flex">
          <div className="relative flex w-full rounded-md border border-zinc-200 focus-within:border-rose-400 transition-colors overflow-hidden">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What are you looking for?"
              className="flex-1 px-4 py-3 text-sm outline-none"
            />
            <button type="submit" className="bg-zinc-900 text-white px-6 text-sm font-medium flex items-center gap-2 hover:bg-rose-500 transition-colors">
              <Search className="w-4 h-4" /> Search
            </button>
          </div>
        </form>

        <div className="flex items-center gap-5 ml-auto">
          <div className="hidden md:block text-right">
            <div className="text-xs text-zinc-500">Hotline:</div>
            <a href={`tel:${COMPANY.phoneRaw}`} className="text-base font-semibold text-zinc-900 flex items-center gap-1">
              <Phone className="w-4 h-4 text-rose-500" /> {COMPANY.phone}
            </a>
          </div>
          {user ? (
            <div className="hidden md:block relative">
              <button
                onClick={() => setUserMenu((s) => !s)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white font-bold text-sm hover:shadow-lg transition-all"
              >
                {(user.firstName || "D")[0].toUpperCase()}
              </button>
              {userMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenu(false)} />
                  <div className="absolute right-0 mt-2 w-60 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 py-2">
                    <div className="px-4 py-2 border-b border-zinc-100">
                      <div className="text-sm font-semibold text-zinc-900 truncate">{user.firstName} {user.lastName}</div>
                      <div className="text-xs text-zinc-500 truncate">{user.email}</div>
                    </div>
                    <Link to="/account" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-rose-50 hover:text-rose-600 transition-colors">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </Link>
                    <Link to="/account/orders" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-rose-50 hover:text-rose-600 transition-colors">
                      <ShoppingCart className="w-4 h-4" /> My Orders
                    </Link>
                    <Link to="/account/designs" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-rose-50 hover:text-rose-600 transition-colors">
                      <Heart className="w-4 h-4" /> Saved Designs
                    </Link>
                    <button onClick={() => { signOut(); setUserMenu(false); navigate("/"); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-rose-50 hover:text-rose-600 transition-colors border-t border-zinc-100 mt-1 pt-2">
                      <LogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link to="/signin" className="hidden md:flex w-10 h-10 items-center justify-center rounded-full border border-zinc-200 hover:border-rose-400 hover:text-rose-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
          )}
          <Link to="/wishlist" className="hidden md:flex w-10 h-10 items-center justify-center rounded-full border border-zinc-200 hover:border-rose-400 hover:text-rose-500 transition-colors relative">
            <Heart className="w-5 h-5" />
          </Link>
          <Link to="/cart" className="relative w-10 h-10 flex items-center justify-center rounded-full border border-zinc-200 hover:border-rose-400 hover:text-rose-500 transition-colors">
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">{count}</span>
            )}
          </Link>
          <button className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* nav */}
      <nav className="max-w-7xl mx-auto px-4 pb-3 flex items-center justify-between flex-wrap gap-3">
        <ul className="flex items-center gap-6 text-sm font-medium">
          <li><Link to="/" className="text-rose-500">Home</Link></li>
          <li><Link to="/about" className="text-zinc-700 hover:text-rose-500 transition-colors">About Us</Link></li>
          <li><Link to="/shop" className="text-zinc-700 hover:text-rose-500 transition-colors">Shop</Link></li>
          <li><Link to="/quote" className="text-zinc-700 hover:text-rose-500 transition-colors">Request a Quote</Link></li>
          <li><Link to="/promo" className="text-zinc-700 hover:text-rose-500 transition-colors">Monthly Promo</Link></li>
          <li><Link to="/contact" className="text-zinc-700 hover:text-rose-500 transition-colors">Contact Us</Link></li>
        </ul>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-zinc-100 bg-white">
          <form onSubmit={onSearch} className="p-4">
            <div className="flex rounded-md border border-zinc-200 overflow-hidden">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products" className="flex-1 px-3 py-2 text-sm outline-none" />
              <button className="bg-zinc-900 text-white px-4 text-sm">Go</button>
            </div>
          </form>
          <ul className="px-4 pb-4 space-y-2 text-sm">
            <li><Link to="/" onClick={() => setMobileOpen(false)}>Home</Link></li>
            <li><Link to="/about" onClick={() => setMobileOpen(false)}>About</Link></li>
            <li><Link to="/shop" onClick={() => setMobileOpen(false)}>Shop</Link></li>
            <li><Link to="/quote" onClick={() => setMobileOpen(false)}>Request a Quote</Link></li>
            <li><Link to="/promo" onClick={() => setMobileOpen(false)}>Monthly Promo</Link></li>
            <li><Link to="/contact" onClick={() => setMobileOpen(false)}>Contact</Link></li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;
